import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useWalkRecorder — client-side recorder for a trail follow session.
 *
 * Reuses `useLiveTracking` on the host component (we never spin up our
 * own `watchPosition` to avoid two concurrent geolocation watchers).
 * The host passes in the current `userPos` and the hook records a fix
 * every ~15 s OR when the walker has moved ~15 m since the last fix,
 * whichever comes first.
 *
 * Fixes + the session envelope are mirrored to localStorage so a tab
 * reload / crash doesn't lose a walk. The host checks `getResumable()`
 * on mount to offer "Resume your walk on <trail>".
 */

const STORAGE_KEY = "td_walk_in_progress";
const FIX_INTERVAL_MS = 15_000;
const FIX_DISTANCE_M = 15.0;

const EARTH_M = 6_371_008.8;
function haversineM(lat1, lng1, lat2, lng2) {
  const r1 = (lat1 * Math.PI) / 180;
  const r2 = (lat2 * Math.PI) / 180;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(r1) * Math.cos(r2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_M * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function readSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeSession(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}
function clearSession() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

export function getResumableWalk() {
  const s = readSession();
  if (!s || !s.active || !s.trail_id) return null;
  // Guard against a stale session that's been "active" for over 24 h —
  // treat it as abandoned so the resume prompt doesn't haunt us and the
  // raw fixes don't linger on disk indefinitely (privacy: phase 2).
  const started = Date.parse(s.started_at);
  if (Number.isFinite(started) && Date.now() - started > 24 * 60 * 60 * 1000) {
    clearSession();
    return null;
  }
  return s;
}

export function purgeStaleWalk() {
  // Idempotent app-open guard. Reads once; if the saved session is
  // older than 24 h (or malformed) it's wiped. Safe to call eagerly on
  // every mount — no-op when nothing is stored.
  const s = readSession();
  if (!s) return;
  const started = Date.parse(s?.started_at || "");
  if (!s.active || !s.trail_id || !Number.isFinite(started) || Date.now() - started > 24 * 60 * 60 * 1000) {
    clearSession();
  }
}

export function clearResumableWalk() {
  clearSession();
}

export function useWalkRecorder({ userPos, trail }) {
  const [walking, setWalking] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const lastFixRef = useRef(null); // { t, lat, lng }
  const fixesRef = useRef([]);
  const tickRef = useRef(0);

  // Rehydrate on mount if a session was in flight for THIS trail.
  useEffect(() => {
    const s = readSession();
    if (s && s.active && s.trail_id === trail?.location_id) {
      setWalking(true);
      setStartedAt(s.started_at);
      fixesRef.current = Array.isArray(s.fixes) ? s.fixes : [];
      lastFixRef.current = fixesRef.current[fixesRef.current.length - 1] || null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trail?.location_id]);

  const recordFix = useCallback(() => {
    if (!userPos || !userPos.position) return;
    const [lat, lng] = userPos.position;
    const now = Date.now();
    const nowIso = new Date(now).toISOString();
    const last = lastFixRef.current;
    // 15 s OR 15 m — whichever triggers first — so slow walkers still
    // get regular pings on a bench, and fast walkers don't blow 2000.
    if (last) {
      const dtMs = now - Date.parse(last.t);
      const dM = haversineM(last.lat, last.lng, lat, lng);
      if (dtMs < FIX_INTERVAL_MS && dM < FIX_DISTANCE_M) return;
    }
    const fix = { t: nowIso, lat, lng, accuracy: userPos.accuracy ?? null };
    fixesRef.current.push(fix);
    lastFixRef.current = fix;
    // Trim to spec cap minus a safety margin — the server rejects >2000.
    if (fixesRef.current.length > 1990) fixesRef.current.splice(0, fixesRef.current.length - 1990);
    const s = readSession();
    if (s && s.active) writeSession({ ...s, fixes: fixesRef.current });
  }, [userPos]);

  // When the GPS watch delivers a new position, consider recording.
  useEffect(() => {
    if (!walking) return;
    recordFix();
  }, [walking, userPos, recordFix]);

  // Secondary 15 s timer so a stationary rest still times through.
  useEffect(() => {
    if (!walking) return;
    tickRef.current = window.setInterval(recordFix, FIX_INTERVAL_MS);
    return () => window.clearInterval(tickRef.current);
  }, [walking, recordFix]);

  const start = useCallback(() => {
    if (!trail?.location_id) return;
    const nowIso = new Date().toISOString();
    fixesRef.current = [];
    lastFixRef.current = null;
    writeSession({
      active: true,
      trail_id: trail.location_id,
      trail_name: trail.name || "",
      started_at: nowIso,
      fixes: [],
    });
    setStartedAt(nowIso);
    setWalking(true);
  }, [trail]);

  const cancel = useCallback(() => {
    clearSession();
    fixesRef.current = [];
    lastFixRef.current = null;
    setWalking(false);
    setStartedAt(null);
  }, []);

  const finishPayload = useCallback(() => {
    const nowIso = new Date().toISOString();
    return {
      trail_id: trail?.location_id,
      started_at: startedAt,
      finished_at: nowIso,
      fixes: fixesRef.current.slice(0, 2000),
    };
  }, [trail, startedAt]);

  const complete = useCallback(() => {
    clearSession();
    fixesRef.current = [];
    lastFixRef.current = null;
    setWalking(false);
    const sa = startedAt;
    setStartedAt(null);
    return sa;
  }, [startedAt]);

  return {
    walking,
    startedAt,
    start,
    cancel,
    complete,
    finishPayload,
    fixCount: fixesRef.current.length,
  };
}
